import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Icon } from "@/components/Icon";
import { useBetSlip } from "@/context/BetSlipContext";
import { SBOddsButton } from "@/components/sportsbook/sb";
import { hasStartedPregame } from "@/lib/sportsbookPriority";
import LoadError from "@/components/ui/LoadError";
import EmptyState from "@/components/ui/EmptyState";

function startLabel(value) {
  if (!value) return "Start time TBA";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function unavailableLabel(o) {
  if (o.sync_state === "removed_upstream_needs_resolution") return "Unavailable · operator review pending";
  if (o.sync_state === "resolved_void" || o.sync_resolution === "void") return "Voided · did not participate";
  if (o.sync_state === "resolved_eliminated" || o.sync_resolution === "eliminated") return "Eliminated";
  if (o.suspended) return "Suspended";
  if (o.active === false) return "Unavailable";
  return null;
}

export default function OutrightDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toggle, has } = useBetSlip();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("price");

  const pull = useCallback(async (silent = false) => {
    try {
      const { data } = await api.get(`/events/${id}`);
      if (!aliveRef.current) return;
      if (data.event_type !== "outright") {
        navigate(`/event/${id}`, { replace: true });
        return;
      }
      setEvent(data);
      setLoadErr(false);
    } catch (e) {
      // A hard failure with nothing on screen gets an in-place retry (not just a toast);
      // a background refresh keeps the last-good board.
      if (!silent && aliveRef.current) setLoadErr(true);
    } finally { if (aliveRef.current) setLoading(false); }
  }, [id, navigate]);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    pull();
    const timer = setInterval(() => pull(true), 5000);
    return () => { aliveRef.current = false; clearInterval(timer); };
  }, [pull]);

  const visibleOutcomes = useMemo(() => (event?.outcomes || []).filter((o) => !o.hidden), [event]);
  const activeCount = useMemo(() => visibleOutcomes.filter((o) => o.active !== false && o.suspended !== true).length, [visibleOutcomes]);
  const unavailableCount = visibleOutcomes.length - activeCount;

  const outcomes = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = visibleOutcomes.filter((o) => !q || String(o.label || "").toLowerCase().includes(q));
    const availability = (o) => (o.active !== false && o.suspended !== true ? 0 : 1);
    if (sort === "alpha") {
      rows = [...rows].sort((a, b) => availability(a) - availability(b) || String(a.label).localeCompare(String(b.label)));
    } else {
      rows = [...rows].sort((a, b) => availability(a) - availability(b) || Number(a.odds || 99999) - Number(b.odds || 99999) || String(a.label).localeCompare(String(b.label)));
    }
    return rows;
  }, [visibleOutcomes, query, sort]);

  const pick = (o) => {
    if (!event || o.active === false || o.suspended === true) return;
    toggle({
      eventId: event.id || id,
      outcomeId: String(o.id),
      label: o.label,
      odds: o.odds,
      marketVersion: o.market_version ?? event.outcomes_rev ?? null,
      line: o.line ?? null,
      side: o.side ?? null,
      eventLabel: event.title || "Outright",
      market: o.market || "moneyline",
      league: event.league || null,
      sport: event.sport || null,
      live: event.status === "live",
    });
  };

  if (loading && !event) return (
    <div className="px-4 pb-[120px] pt-6 text-white sm:px-6 lg:pr-[360px]" data-testid="outright-loading">
      <div className="mx-auto max-w-6xl space-y-4" aria-hidden="true">
        <div className="h-3 w-40 animate-pulse rounded bg-surface-4" />
        <div className="h-[132px] animate-pulse rounded-[22px] bg-surface-3" />
        <div className="flex gap-2"><div className="h-11 flex-1 animate-pulse rounded-xl bg-surface-4" /><div className="h-11 w-32 animate-pulse rounded-xl bg-surface-4" /></div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-[58px] animate-pulse rounded-[14px] bg-surface-3" />)}
        </div>
      </div>
    </div>
  );
  if (!event) return (
    <div className="mx-auto max-w-2xl px-4 pb-[120px] pt-16 lg:pr-[360px]">
      {loadErr
        ? <LoadError onRetry={() => { setLoading(true); pull(); }} message="We couldn't load this market." testid="outright-load-error" />
        : <EmptyState icon="all-sports" message="This market is no longer available." action={{ label: "Back to Sportsbook", icon: "arrow-left", onClick: () => navigate("/sportsbook") }} testid="outright-not-found" />}
    </div>
  );

  const locked = !["open", "live"].includes(event.status) || hasStartedPregame(event);
  const favorite = [...visibleOutcomes]
    .filter((o) => o.active !== false && o.suspended !== true)
    .sort((a, b) => Number(a.odds || 99999) - Number(b.odds || 99999) || String(a.label).localeCompare(String(b.label)))[0];

  return (
    <div className="min-h-screen overflow-x-clip px-4 pb-[120px] pt-6 text-white sm:px-6 lg:pr-[360px]">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex items-center gap-1.5 text-xs text-[#71717a]">
          <Link to="/sportsbook" className="inline-flex items-center gap-1 hover:text-white"><Icon name="arrow-left" size={14} /> Sportsbook</Link>
          <span>/</span><span className="uppercase tracking-wide">{event.league || event.sport}</span><span>/</span><span className="truncate text-[#a1a1aa]">Outright</span>
        </div>

        <section className="surface-premium overflow-hidden rounded-[22px]">
          <div className="relative overflow-hidden px-4 py-5 sm:px-6 sm:py-6">
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#00D632]/[.07] blur-3xl" />
            <div className="relative flex flex-wrap items-start gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-[10px] font-extrabold uppercase tracking-[.18em] text-[#00D632]"><span>Outright Market</span></div>
                <h1 className="mt-2 max-w-3xl font-display text-[28px] font-extrabold leading-[1.05] tracking-[-.04em] sm:text-[38px]">{event.title}</h1>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#71717a]"><span>{event.sport} · {event.league}</span><span>{startLabel(event.start_time)}</span><span>{activeCount} active selections</span>{unavailableCount > 0 && <span>{unavailableCount} unavailable</span>}{event.max_bet ? <span>Max bet ${Number(event.max_bet).toFixed(0)}</span> : null}</div>
              </div>
              <div className={`rounded-xl px-3 py-2 text-right ${event.status === "open" ? "bg-[#00D632]/10" : "bg-white/[.05]"}`}><div className="text-[9px] font-bold uppercase tracking-[.12em] text-[#71717a]">Status</div><div className={`mt-0.5 text-[12px] font-extrabold uppercase ${event.status === "open" ? "text-[#00D632]" : "text-[#a1a1aa]"}`}>{event.status}</div></div>
            </div>
          </div>
          {locked && <div className="border-t border-[#1F1F23] bg-[#141417] px-4 py-3 text-[12px] text-[#a1a1aa]">Betting is currently closed for this market. Existing tickets remain visible in My Bets.</div>}
        </section>

        {favorite && !query && <section className="mt-5 rounded-[18px] border border-[#00D632]/20 bg-[#00D632]/[.035] p-4 sm:p-5">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[.14em] text-[#00D632]">Current favorite</div>
          <div className="flex items-center gap-3"><div className="min-w-0 flex-1 font-display text-[17px] font-extrabold">{favorite.label}</div><div className="w-[150px]"><SBOddsButton label="Select" price={favorite.odds} size="md" selected={has(event.id || id, favorite.id)} locked={locked} onClick={() => pick(favorite)} /></div></div>
        </section>}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1"><Icon name="search" size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#71717a]" /><input value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search entrants" placeholder={`Search ${visibleOutcomes.length} teams / entrants…`} className="h-11 w-full rounded-xl border border-[#26262B] bg-[#111114] pl-9 pr-3 text-[13px] text-white outline-none placeholder:text-[#52525b] focus:border-pnr-green/50 focus-ring" /></div>
          <button onClick={() => setSort(sort === "price" ? "alpha" : "price")} aria-label={`Sort: ${sort === "price" ? "favorites first" : "A to Z"}`} className="flex h-11 items-center gap-2 rounded-xl border border-[#26262B] bg-[#111114] px-3.5 text-[12px] font-bold text-[#d4d4d8] transition-colors hover:border-border-strong focus-ring"><Icon name="layers" size={14} /> {sort === "price" ? "Favorites first" : "A–Z"}</button>
        </div>

        <div className="mt-3 flex items-center justify-between"><h2 className="font-display text-[18px] font-extrabold">All selections</h2><span className="text-[11px] text-[#71717a]">{outcomes.length} shown</span></div>
        {outcomes.length === 0 ? <div className="mt-3 rounded-[18px] border border-dashed border-[#26262B] py-14 text-center text-[13px] text-[#71717a]">No entrant matches your search.</div> : <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {outcomes.map((o) => {
            const unavailable = unavailableLabel(o);
            const selectionLocked = locked || o.suspended === true || o.active === false;
            return <div key={o.id} className={`flex items-center gap-3 rounded-[14px] border bg-[#111114] p-3 transition-colors ${selectionLocked ? "border-[#1A1A1E] opacity-75" : "border-[#1F1F23] hover:border-[#2F2F35]"}`}><div className="min-w-0 flex-1"><div className="truncate text-[13px] font-bold text-white">{o.label}</div>{unavailable && <div className={`mt-0.5 text-[9px] font-bold uppercase ${o.sync_state === "removed_upstream_needs_resolution" ? "text-[#F59E0B]" : "text-[#71717a]"}`}>{unavailable}</div>}</div><div className="w-[118px]"><SBOddsButton label="" price={o.odds} size="sm" selected={!selectionLocked && has(event.id || id, o.id)} locked={selectionLocked} onClick={() => pick(o)} /></div></div>;
          })}
        </div>}
      </div>
    </div>
  );
}
